import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import {
  createRegion,
  getRegion,
  listUsersLite,
  listZones,
  updateRegion,
  listRegionDistricts,
  setRegionDistricts,
  type UserLite,
  type ZoneRow,
} from '../../api/orgApi.js';
import { Field, FormShell, SaveBar, SelectField, todayIso } from './orgKit.js';
import { MembershipPicker } from './MembershipPicker.js';

export function RegionFormPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { regionId } = useParams<{ regionId: string }>();
  const editing = Boolean(regionId);

  const [regionCode, setRegionCode] = useState('');
  const [regionName, setRegionName] = useState('');
  const [parentZoneId, setParentZoneId] = useState('');
  const [regionHeadId, setRegionHeadId] = useState('');
  const [effectiveFromDate, setEffectiveFromDate] = useState(todayIso());
  const [zones, setZones] = useState<ZoneRow[]>([]);
  const [users, setUsers] = useState<UserLite[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listZones(token).then((r) => setZones(r.data)).catch((e) => setError((e as Error).message));
    listUsersLite(token).then((r) => setUsers(r.data)).catch((e) => setError((e as Error).message));
  }, [token]);

  useEffect(() => {
    if (!regionId) return;
    getRegion(token, regionId)
      .then((r) => {
        setRegionCode(r.regionCode);
        setRegionName(r.regionName);
        setParentZoneId(r.parentZoneId ?? '');
        setRegionHeadId(r.regionHeadId ?? '');
        setEffectiveFromDate(r.effectiveFromDate ?? todayIso());
      })
      .catch((e) => setError((e as Error).message));
  }, [token, regionId]);

  async function submit(): Promise<void> {
    if (!regionName.trim()) { setError(t('org.region.form.nameReq')); return; }
    if (!parentZoneId) { setError(t('org.region.form.zoneReq')); return; }
    if (!editing && !regionCode.trim()) { setError(t('org.region.form.codeReq')); return; }
    setError(null);
    setBusy(true);
    try {
      if (editing && regionId) {
        await updateRegion(token, regionId, {
          regionName: regionName.trim(),
          parentZoneId,
          regionHeadId: regionHeadId || undefined,
        });
      } else {
        await createRegion(token, {
          regionCode: regionCode.trim(),
          regionName: regionName.trim(),
          parentZoneId,
          regionHeadId: regionHeadId || undefined,
          effectiveFromDate,
        });
      }
      navigate('/org/regions');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const zoneOptions = zones.map((z) => ({ value: z.zoneId, label: `${z.zoneCode} — ${z.zoneName}` }));
  const userOptions = users.map((u) => ({ value: u.userId, label: u.displayName }));

  return (
    <FormShell label="region-form" title={editing ? t('org.region.form.editTitle') : t('org.region.form.newTitle')} error={error} onSubmit={submit}>
      <Field label={t('org.field.code')} name="regionCode" value={regionCode} onChange={setRegionCode} required placeholder={t('org.region.form.codePh')} />
      <Field label={t('org.field.name')} name="regionName" value={regionName} onChange={setRegionName} required />
      <SelectField label={t('org.field.parentZone')} name="parentZoneId" value={parentZoneId} onChange={setParentZoneId} options={zoneOptions} required />
      <SelectField label={t('org.field.regionHead')} name="regionHeadId" value={regionHeadId} onChange={setRegionHeadId} options={userOptions} placeholder={t('org.common.noneNone')} />
      {!editing && (
        <Field label={t('org.field.effectiveFrom')} name="effectiveFromDate" value={effectiveFromDate} onChange={setEffectiveFromDate} type="date" required />
      )}
      <SaveBar busy={busy} saveLabel={editing ? t('org.common.saveChanges') : t('org.region.form.create')} cancelTo="/org/regions" />
      {editing && regionId ? (
        <MembershipPicker
          testId="region-districts"
          title={t('org.region.form.districtsTitle')}
          emptyHint={t('org.region.form.districtsEmpty')}
          load={async () =>
            (await listRegionDistricts(token, regionId)).map((d) => ({
              id: d.districtId, code: d.districtCode, name: d.districtName,
              assigned: d.assigned, takenBy: d.assignedToOtherRegionId,
            }))
          }
          save={async (ids) =>
            (await setRegionDistricts(token, regionId, ids)).map((d) => ({
              id: d.districtId, code: d.districtCode, name: d.districtName,
              assigned: d.assigned, takenBy: d.assignedToOtherRegionId,
            }))
          }
        />
      ) : null}
    </FormShell>
  );
}
